import { cn } from "./ui/utils";
import {
  LayoutDashboard,
  QrCode,
  Users,
  Activity,
  Settings,
  Menu,
  LogOut,
  Package,
  MessageSquareText,
  LifeBuoy,
  ReceiptIndianRupee,
  Mail,
  MailOpen,
  Tag,
  Footprints,
} from "lucide-react";
import { Button } from "./ui/button";
import React from "react";
import { useAuth } from "../hooks/useAuth";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface SidebarProps {
  activeScreen: string;
  onScreenChange: (screen: string) => void;
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
}

export function Sidebar({ 
  activeScreen, 
  onScreenChange, 
  isCollapsed, 
  onToggleCollapsed 
}: SidebarProps) {
  const { logout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { id: 'qrcodes', label: 'QR Codes', icon: QrCode, path: '/qrcodes' },
    { id: 'users', label: 'Users', icon: Users, path: '/users' },
    { id: 'visitors', label: 'Visitors', icon: Footprints, path: '/visitors' },
    { id: 'newsletters', label: 'Newsletters', icon: Mail, path: '/newsletters' },
    { id: 'subscribers', label: 'Subscribed Emails', icon: MailOpen, path: '/subscribers' },
    { id: 'orders', label: 'Orders', icon: ReceiptIndianRupee, path: '/orders' },
    { id: 'coupons', label: 'Coupons', icon: Tag, path: '/coupons' },
    { id: 'support-tickets', label: 'Support Tickets', icon: LifeBuoy, path: '/support-tickets' },
    { id: 'feedbacks', label: 'Feedback', icon: MessageSquareText, path: '/feedbacks' },
    { id: 'scanlogs', label: 'Scan Logs', icon: Activity, path: '/scanlogs' },
    { id: 'packages', label: 'Packages', icon: Package, path: '/packages' },
    { id: 'settings', label: 'Settings', icon: Settings, path: '/settings' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div 
      className={cn(
        "h-screen bg-sidebar border-r border-sidebar-border flex flex-col transition-all duration-300",
        isCollapsed ? "w-16" : "w-[220px]"
      )}
    >
      {/* Header with Logo and Toggle */}
      <div className="p-4 border-b border-sidebar-border flex items-center justify-between">
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 via-purple-400 to-pink-400 rounded-lg flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:scale-105">
                <QrCode className="w-7 h-7 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full animate-pulse"></div>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                PingOwner
              </h1>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleCollapsed}
          className="text-sidebar-foreground hover:bg-sidebar-accent"
        >
          <Menu className="w-4 h-4" />
        </Button>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-2">
        <ul className="space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <li key={item.id}>
                <Link
                  to={item.path}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors",
                    "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                    isActive && "bg-sidebar-primary text-sidebar-primary-foreground"
                  )}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!isCollapsed && (
                    <span className="truncate">{item.label}</span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Info and Logout */}
      <div className="p-4 border-t border-sidebar-border">
        {!isCollapsed && user && (
          <div className="mb-3 p-2 rounded-lg bg-sidebar-accent/50">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-xs text-primary-foreground font-medium">
                {user.email.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sidebar-foreground truncate">
                  {user.email}
                </p>
                <p className="text-xs text-sidebar-muted-foreground truncate">
                  {user.admin_type}
                </p>
              </div>
            </div>
          </div>
        )}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="w-full text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <LogOut className="w-4 h-4" />
          {!isCollapsed && (
            <span className="ml-2">Logout</span>
          )}
        </Button>
      </div>
    </div>
  );
}
